/* Bireafon — PDP teardown interaction.
   Progressive enhancement: without this file every component card is open and
   the drawing renders as a plain assembled technical illustration. */
(function () {
  'use strict';

  var TOUR_STEP = 2300;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Teardown(root) {
    this.root = root;
    this.stage = root.querySelector('.bf-td__stage');
    this.pins = Array.prototype.slice.call(root.querySelectorAll('[data-bf-pin]'));
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-bf-card]'));
    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-bf-step]'));
    this.leaders = Array.prototype.slice.call(root.querySelectorAll('[data-leader]'));
    this.dials = Array.prototype.slice.call(root.querySelectorAll('[data-dial]'));
    this.order = this.cards.map(function (c) {
      return c.getAttribute('data-bf-card');
    });
    this.active = null;
    this.tourTimer = null;
    this.touched = false;

    if (!this.stage || !this.cards.length) return;

    this.cardsEl = root.querySelector('.bf-td__cards');
    this.compact = window.matchMedia('(max-width: 989px)');

    root.classList.add('is-ready');
    this.bind();
    this.set(null, true);
    this.reserve();
    this.observe();
  }

  Teardown.prototype.bind = function () {
    var self = this;

    this.pins.forEach(function (pin, i) {
      pin.addEventListener('click', function () {
        self.stop();
        self.toggle(pin.getAttribute('data-bf-pin'));
      });
      pin.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        self.stop();
        var next = self.pins[(i + dir + self.pins.length) % self.pins.length];
        next.focus();
        self.set(next.getAttribute('data-bf-pin'));
      });
    });

    this.cards.forEach(function (card) {
      var trigger = card.querySelector('[data-bf-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        self.stop();
        self.toggle(card.getAttribute('data-bf-card'));
      });
    });

    this.steps.forEach(function (step, i) {
      step.addEventListener('click', function () {
        self.stop();
        self.set(step.getAttribute('data-bf-step'));
      });
      step.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        self.stop();
        var next = self.steps[(i + dir + self.steps.length) % self.steps.length];
        next.focus();
        self.set(next.getAttribute('data-bf-step'));
      });
    });

    // keep an open panel correctly sized when the column reflows
    var raf;
    window.addEventListener('resize', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        self.cards.forEach(function (card) {
          var panel = card.querySelector('.bf-card__panel');
          if (panel && card.classList.contains('is-active')) panel.style.height = 'auto';
        });
        self.reserve();
        // mobile has no empty state, so make sure something is selected
        if (self.compact.matches && !self.active) self.set(self.order[0]);
      });
    });
  };

  Teardown.prototype.toggle = function (part) {
    if (this.compact.matches) {
      this.set(part);
      return;
    }
    this.set(this.active === part ? null : part);
  };

  Teardown.prototype.set = function (part, silent) {
    var self = this;
    this.active = part;

    if (part) {
      this.stage.setAttribute('data-active', part);
    } else {
      this.stage.removeAttribute('data-active');
    }

    this.pins.forEach(function (pin) {
      pin.setAttribute('aria-expanded', String(pin.getAttribute('data-bf-pin') === part));
    });

    this.leaders.forEach(function (l) {
      l.classList.toggle('is-on', l.getAttribute('data-leader') === part);
    });

    this.dials.forEach(function (d) {
      d.classList.toggle('is-on', d.getAttribute('data-dial') === part);
    });

    this.steps.forEach(function (step) {
      step.setAttribute('aria-pressed', String(step.getAttribute('data-bf-step') === part));
    });

    this.cards.forEach(function (card) {
      var on = card.getAttribute('data-bf-card') === part;
      var trigger = card.querySelector('[data-bf-trigger]');
      var panel = card.querySelector('.bf-card__panel');
      card.classList.toggle('is-active', on);
      if (trigger) trigger.setAttribute('aria-expanded', String(on));
      if (!panel) return;

      if (silent) {
        panel.style.height = on ? 'auto' : '0px';
        return;
      }

      if (on) {
        panel.style.height = panel.scrollHeight + 'px';
        self.after(panel, function () {
          if (card.classList.contains('is-active')) panel.style.height = 'auto';
        });
      } else if (panel.style.height !== '0px') {
        panel.style.height = panel.scrollHeight + 'px';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (!card.classList.contains('is-active')) panel.style.height = '0px';
          });
        });
      }
    });
  };

  /* Mobile shows one component at a time, so the column would grow and shrink as
     the visitor switches. Measure the column with each component selected in turn
     and reserve the tallest, so nothing below it ever jumps. */
  Teardown.prototype.reserve = function () {
    if (!this.cardsEl) return;
    var self = this;
    var was = this.active;

    this.cardsEl.style.removeProperty('--bf-cardmin');
    if (!this.compact.matches) return;

    var tallest = 0;
    this.order.forEach(function (part) {
      self.set(part, true);
      tallest = Math.max(tallest, self.cardsEl.offsetHeight);
    });

    if (tallest) this.cardsEl.style.setProperty('--bf-cardmin', tallest + 'px');
    this.set(was, true);
  };

  Teardown.prototype.after = function (el, fn) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', run);
      fn();
    }
    el.addEventListener('transitionend', run);
    setTimeout(run, 700);
  };

  Teardown.prototype.stop = function () {
    this.touched = true;
    this.root.classList.add('is-touched');
    if (this.tourTimer) {
      clearTimeout(this.tourTimer);
      this.tourTimer = null;
    }
  };

  Teardown.prototype.tour = function () {
    var self = this;
    var i = 0;

    function step() {
      if (self.touched) return;
      self.set(self.order[i]);
      i += 1;
      if (i < self.order.length) {
        self.tourTimer = setTimeout(step, TOUR_STEP);
      } else {
        // settle on the first component so the reader lands on a full card
        self.tourTimer = setTimeout(function () {
          if (!self.touched) self.set(self.order[0]);
        }, TOUR_STEP);
      }
    }

    step();
  };

  Teardown.prototype.observe = function () {
    var self = this;
    var wantsTour = this.root.getAttribute('data-bf-tour') === 'true' && !reducedMotion();

    this.set(this.order[0], true);
    if (!wantsTour) return;

    if (!('IntersectionObserver' in window)) {
      this.tour();
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          self.tour();
        });
      },
      { threshold: 0.4 }
    );
    io.observe(this.stage);
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-bf-teardown]').forEach(function (el) {
      if (el.dataset.bfInit) return;
      el.dataset.bfInit = '1';
      new Teardown(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // theme editor re-renders a section in place
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
